import { extractSlug, fetchPageContent } from './scraper';
import { saveChapter, OfflineChapter } from './mangaOfflineDB';

export interface DownloadProgress {
  chapterNum: number;
  currentImage: number;
  totalImages: number;
  status: 'fetching_page' | 'downloading_images' | 'saving' | 'completed' | 'error';
  message: string;
  error?: string;
}

export interface DownloadChapterParams {
  mangaId: string;
  mangaTitle: string;
  chapterNum: number;
  sourceUrl: string;
  sourceName: string;
  onProgress?: (progress: DownloadProgress) => void;
  abortSignal?: AbortSignal;
  /** Other sites of the same manga, tried in order when the chapter is missing on sourceUrl */
  fallbackSources?: { url: string; name: string }[];
}

/**
 * In-memory cache of scraped chapter URLs from manga overview pages
 * Key: sourceUrl (manga main page), Value: Map<chapterNum, exactChapterUrl>
 */
const overviewChapterCache = new Map<string, Map<number, string>>();

/**
 * Scrape all exact chapter links from an overview page text
 */
export function extractChapterMapFromOverview(
  pageText: string,
  sourceUrl: string
): Map<number, string> {
  const map = new Map<number, string>();
  if (!pageText) return map;

  let baseOrigin = '';
  try {
    baseOrigin = new URL(sourceUrl).origin;
  } catch {}

  // 1. Markdown links from Jina Reader: [Text](URL)
  const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s\)\"]+)\)/gi;
  let match: RegExpExecArray | null;
  while ((match = mdLinkRegex.exec(pageText)) !== null) {
    const text = match[1];
    let url = match[2];
    const chInText = text.match(/(?:ตอนที่?|chapter|ep|ch|ตอนใหม่ตอนที่?)\s*(\d+(?:\.\d+)?)/i);
    const chInUrl = url.match(/(?:ตอนที่?|chapter|ep|ch)[-_/]?(\d+(?:\.\d+)?)/i);
    const ch = chInText ? parseFloat(chInText[1]) : (chInUrl ? parseFloat(chInUrl[1]) : null);
    if (ch && !map.has(ch)) {
      map.set(ch, url);
    }
  }

  // 2. HTML links: <a href="URL">Text</a>
  const htmlLinkRegex = /<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  while ((match = htmlLinkRegex.exec(pageText)) !== null) {
    let url = match[1];
    if (url.startsWith('/') && baseOrigin) url = `${baseOrigin}${url}`;
    const text = match[2].replace(/<[^>]+>/g, ' ');
    const chInText = text.match(/(?:ตอนที่?|chapter|ep|ch|ตอนใหม่ตอนที่?)\s*(\d+(?:\.\d+)?)/i);
    const chInUrl = url.match(/(?:ตอนที่?|chapter|ep|ch)[-_/]?(\d+(?:\.\d+)?)/i);
    const ch = chInText ? parseFloat(chInText[1]) : (chInUrl ? parseFloat(chInUrl[1]) : null);
    if (ch && !map.has(ch)) {
      map.set(ch, url);
    }
  }

  return map;
}

/**
 * Predict or generate potential chapter URLs for a source as fallback
 */
export function getPotentialChapterUrls(sourceUrl: string, chapterNum: number): string[] {
  const is1668 = sourceUrl.includes('1668manga.com');
  const isUp = sourceUrl.includes('up-manga.com');
  const isBlackcat = sourceUrl.includes('mangablackcat.com');

  const slug = extractSlug(sourceUrl);
  if (!slug) return [];

  // Remove trailing slashes and queries from slug, and strip chapter suffixes if present
  let cleanSlug = slug.replace(/[#?].*$/, '').replace(/\/+$/, '');
  cleanSlug = cleanSlug.replace(/-(?:ตอนที่|chapter|ep|ch)-\d+(?:[.-]\d+)?$/i, '');
  cleanSlug = cleanSlug.replace(/\/(?:ตอนที่|chapter|ep|ch)?\/?\d+(?:[.-]\d+)?$/i, '');

  if (is1668) {
    return [
      `https://1668manga.com/${cleanSlug}-ตอนที่-${chapterNum}/`,
      `https://1668manga.com/${cleanSlug}-ตอนที่${chapterNum}/`,
      `https://1668manga.com/manga/${cleanSlug}/${chapterNum}/`,
      `https://1668manga.com/${cleanSlug}-chapter-${chapterNum}/`,
    ];
  } else if (isUp) {
    return [
      `https://www.up-manga.com/${cleanSlug}-ตอนที่-${chapterNum}/`,
      `https://www.up-manga.com/${cleanSlug}-ep-${chapterNum}/`,
      `https://www.up-manga.com/${cleanSlug}-ตอนที่${chapterNum}/`,
      `https://www.up-manga.com/manga/${cleanSlug}/${chapterNum}/`,
      `https://www.up-manga.com/${cleanSlug}-chapter-${chapterNum}/`,
    ];
  } else if (isBlackcat) {
    return [
      `https://mangablackcat.com/manga/${cleanSlug}/${chapterNum}`,
      `https://mangablackcat.com/manga/${cleanSlug}/chapter-${chapterNum}`,
    ];
  }

  // Generic fallback
  return [
    `${sourceUrl.replace(/\/+$/, '')}/${chapterNum}/`,
    `${sourceUrl.replace(/\/+$/, '')}-ตอนที่-${chapterNum}/`,
  ];
}

/**
 * Filter out ads, gambling banners, social icons, widgets, and non-manga images
 */
export function isAdOrNonMangaImage(
  url: string,
  linkTarget: string = '',
  altText: string = '',
  baseOrigin: string = ''
): boolean {
  if (!url || typeof url !== 'string') return true;
  const lowerUrl = url.toLowerCase();
  const cleanPath = lowerUrl.split(/[?#]/)[0];
  const lowerLink = (linkTarget || '').toLowerCase();
  const lowerAlt = (altText || '').toLowerCase();

  // 1. Block non-manga formats (GIFs, SVGs are always ads, stickers, or spinners)
  if (cleanPath.endsWith('.gif') || cleanPath.endsWith('.svg')) return true;

  // 2. Block website header logo links (links to homepage / or origin)
  if (
    baseOrigin &&
    (lowerLink === baseOrigin.toLowerCase() ||
      lowerLink === `${baseOrigin.toLowerCase()}/` ||
      lowerLink === '/' ||
      lowerLink === '#')
  ) {
    return true;
  }

  // 3. Block if wrapped in a link to external ad/gambling/affiliate/social target
  if (lowerLink) {
    const isAdLink =
      lowerLink.includes('register') ||
      lowerLink.includes('affiliate') ||
      lowerLink.includes('bit.ly') ||
      lowerLink.includes('ibit.ly') ||
      lowerLink.includes('line.me') ||
      lowerLink.includes('facebook') ||
      lowerLink.includes('twitter') ||
      lowerLink.includes('bet') ||
      lowerLink.includes('slot') ||
      lowerLink.includes('jili') ||
      lowerLink.includes('casino') ||
      lowerLink.includes('lotto') ||
      lowerLink.includes('ufa') ||
      lowerLink.includes('panama') ||
      lowerLink.includes('mahagame') ||
      lowerLink.includes('fan/register');
    if (isAdLink) return true;
  }

  // 4. Block theme assets, plugin assets, avatars, UI counters, gravatar
  if (
    lowerUrl.includes('gravatar.com') ||
    lowerUrl.includes('/themes/') ||
    lowerUrl.includes('/plugins/') ||
    lowerUrl.includes('/assets/') ||
    lowerUrl.includes('readerarea') ||
    lowerUrl.includes('sticky')
  ) {
    return true;
  }

  // 5. Block standalone ad keywords (without false positive on 'uploads'!)
  if (
    /(?:^|[-_/?.])(?:ads?|advertisement|banner|logo|avatar|favicon|icon|badge|counter|button)(?:[-_/.]|$)/i.test(
      cleanPath
    )
  ) {
    return true;
  }

  // 6. Block known gambling & betting keywords
  if (
    /(?:jili|luckmak|sexy365|ufafat|panama888|lotto77|mahagame|pgslot|casino|slot|bet)/i.test(
      cleanPath
    )
  ) {
    return true;
  }

  // 7. Block common ad banner dimensions (e.g. 728x200, 728x90, 140x250, 300x250, 300x600, 160x600, 468x60)
  if (/\b\d{2,3}x\d{2,3}\b/i.test(cleanPath)) return true;

  // 8. Block social icons & close buttons & header logos
  if (
    /(?:facebook|twitter|discord|telegram|instagram)/i.test(cleanPath) ||
    /close\.png$/i.test(cleanPath) ||
    /\/v3\.png$/i.test(cleanPath)
  ) {
    return true;
  }

  // 9. Block site brand logos mentioned in alt text
  if (
    lowerAlt.includes('logo') ||
    lowerAlt.includes('up-manga') ||
    lowerAlt.includes('1668manga') ||
    lowerAlt.includes('mangablackcat') ||
    lowerAlt.includes('avatar')
  ) {
    return true;
  }

  // 10. Must be a valid image file extension
  if (!/\.(jpe?g|png|webp|avif)$/i.test(cleanPath)) return true;

  return false;
}

function decodeBase64Utf8(b64: string): string {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
}

/** JSON argument of ts_reader.run(...), from inline scripts or base64 data: scripts */
export function findTsReaderPayload(content: string): string | null {
  const inline = content.match(/ts_reader\.run\(([\s\S]*?)\);/);
  if (inline && inline[1]) return inline[1];

  const dataScriptRegex = /src=["']data:text\/javascript;base64,([A-Za-z0-9+/=]+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = dataScriptRegex.exec(content)) !== null) {
    const js = decodeBase64Utf8(m[1]);
    const decoded = js.match(/ts_reader\.run\(([\s\S]*?)\);/);
    if (decoded && decoded[1]) return decoded[1];
  }
  return null;
}

/**
 * Extract image URLs from chapter HTML or Jina Reader text with strict ad filtering
 */
export function extractChapterImageUrls(content: string, sourceUrl: string): string[] {
  if (!content) return [];

  let baseOrigin = '';
  try {
    baseOrigin = new URL(sourceUrl).origin;
  } catch {}

  const results: string[] = [];

  // 1. WordPress / TSR / MangaThemesia (Native ts_reader.run JSON payload)
  // 1668manga (Autoptimize) moves the call into <script src="data:text/javascript;base64,...">, so
  // decoded data: scripts are searched too. When the payload exists it is the whole reader: falling
  // through to the generic <img> scan below would only collect the page's ad banners.
  const tsPayload = findTsReaderPayload(content);
  if (tsPayload !== null) {
    try {
      const json = JSON.parse(tsPayload);
      const sources = json.sources || [];
      for (const src of sources) {
        if (Array.isArray(src.images) && src.images.length > 0) {
          for (let u of src.images) {
            if (u.startsWith('//')) u = `https:${u}`;
            if (!isAdOrNonMangaImage(u, '', '', baseOrigin)) {
              results.push(u);
            }
          }
          if (results.length > 0) return Array.from(new Set(results));
        }
      }
      return [];
    } catch {}
  }

  // 2. Markdown images with link detection: [![alt](imgUrl)](linkUrl) OR ![alt](imgUrl)
  const mdRegex =
    /\[!\[(.*?)\]\((https?:\/\/[^\s\)\"]+)\)\]\((https?:\/\/[^\s\)\"]+)\)|!\[(.*?)\]\((https?:\/\/[^\s\)\"]+)\)/gi;
  let match: RegExpExecArray | null;
  while ((match = mdRegex.exec(content)) !== null) {
    const isLinked = Boolean(match[2]);
    const altText = isLinked ? match[1] || '' : match[4] || '';
    const imgUrl = isLinked ? match[2].trim() : match[5].trim();
    const linkUrl = isLinked ? match[3].trim() : '';

    if (!isAdOrNonMangaImage(imgUrl, linkUrl, altText, baseOrigin) && !results.includes(imgUrl)) {
      results.push(imgUrl);
    }
  }

  // 3. HTML <img> tags with surrounding <a> link check
  const htmlImgRegex =
    /(?:<a\b[^>]*href=["']([^"']+)["'][^>]*>\s*)?<img\b[^>]*?(?:data-src|data-lazy-src|data-original|src)=["']([^"']+)["'][^>]*?(?:alt=["']([^"']*)["'])?[^>]*>/gi;
  while ((match = htmlImgRegex.exec(content)) !== null) {
    const linkUrl = match[1] || '';
    let u = match[2].trim();
    const altText = match[3] || '';
    if (u.startsWith('//')) u = `https:${u}`;
    if (u.startsWith('/') && !u.startsWith('//') && baseOrigin) {
      u = `${baseOrigin}${u}`;
    }
    if (!isAdOrNonMangaImage(u, linkUrl, altText, baseOrigin) && !results.includes(u)) {
      results.push(u);
    }
  }

  return results;
}

/**
 * Fetch a single image as binary Blob with CDN referer bypass
 */
export async function downloadImageBlob(
  imgUrl: string,
  abortSignal?: AbortSignal
): Promise<Blob> {
  // Method 1: Try /api/image-proxy
  try {
    const res = await fetch(`/api/image-proxy?url=${encodeURIComponent(imgUrl)}`, {
      signal: abortSignal,
    });
    if (res.ok) {
      const blob = await res.blob();
      if (blob.size > 1000) {
        return blob;
      }
    }
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
  }

  // Method 2: Direct fetch (CORS allowed on many CDNs like mangablackcat)
  try {
    const directRes = await fetch(imgUrl, {
      signal: abortSignal,
      headers: {
        Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
      },
    });
    if (directRes.ok) {
      const blob = await directRes.blob();
      if (blob.size > 1000) {
        return blob;
      }
    }
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
  }

  throw new Error(`ไม่สามารถโหลดรูปภาพ: ${imgUrl.slice(0, 60)}...`);
}

/**
 * Locate a chapter's reader page on one site. Returns null when the site has no readable page for it.
 */
async function findChapterPage(
  chapterNum: number,
  sourceUrl: string,
  sourceName: string,
  onProgress?: (progress: DownloadProgress) => void,
  abortSignal?: AbortSignal
): Promise<{ html: string; url: string } | null> {
  onProgress?.({
    chapterNum,
    currentImage: 0,
    totalImages: 0,
    status: 'fetching_page',
    message: `กำลังค้นหาหน้าตอนที่ ${chapterNum} บน ${sourceName}...`,
  });

  // Step 1: Pre-fetch or retrieve overview page chapter map to get exact URL
  let exactUrl: string | undefined;
  if (!overviewChapterCache.has(sourceUrl)) {
    try {
      onProgress?.({
        chapterNum,
        currentImage: 0,
        totalImages: 0,
        status: 'fetching_page',
        message: `กำลังดึงสารบัญตอนจาก ${sourceName}...`,
      });
      const overviewText = await fetchPageContent(sourceUrl, 12000);
      const map = extractChapterMapFromOverview(overviewText, sourceUrl);
      if (map.size > 0) {
        overviewChapterCache.set(sourceUrl, map);
      }
    } catch (e) {
      console.warn('Failed to pre-cache overview chapter map:', e);
    }
  }

  exactUrl = overviewChapterCache.get(sourceUrl)?.get(chapterNum);

  // Step 2: Build candidate URLs (exact first, then potential patterns)
  const candidateUrls: string[] = [];
  if (exactUrl) {
    candidateUrls.push(exactUrl);
    try {
      candidateUrls.push(encodeURI(decodeURI(exactUrl)));
    } catch {}
  }

  for (const u of getPotentialChapterUrls(sourceUrl, chapterNum)) {
    if (!candidateUrls.includes(u)) candidateUrls.push(u);
    try {
      const enc = encodeURI(u);
      if (!candidateUrls.includes(enc)) candidateUrls.push(enc);
    } catch {}
  }

  for (const url of candidateUrls) {
    if (abortSignal?.aborted) throw new Error('ยกเลิกการดาวน์โหลด');
    try {
      onProgress?.({
        chapterNum,
        currentImage: 0,
        totalImages: 0,
        status: 'fetching_page',
        message: `กำลังเปิดอ่านหน้าตอนที่ ${chapterNum}...`,
      });

      const content = await fetchPageContent(url, 12000);
      if (
        content &&
        content.length > 500 &&
        !content.includes('404: Not Found') &&
        !content.includes('Page not found') &&
        !content.includes('ไม่พบหน้าที่')
      ) {
        const testImages = extractChapterImageUrls(content, url);
        if (testImages.length > 0) {
          return { html: content, url };
        }
      }
    } catch {}
  }

  return null;
}

/**
 * Download an entire chapter and save into IndexedDB
 */
export async function downloadChapter({
  mangaId,
  mangaTitle,
  chapterNum,
  sourceUrl,
  sourceName,
  onProgress,
  abortSignal,
  fallbackSources = [],
}: DownloadChapterParams): Promise<OfflineChapter> {
  const attempts = [{ url: sourceUrl, name: sourceName }, ...fallbackSources.filter((s) => s.url !== sourceUrl)];
  let found: { html: string; url: string } | null = null;
  let usedSourceName = sourceName;

  for (const attempt of attempts) {
    if (abortSignal?.aborted) throw new Error('ยกเลิกการดาวน์โหลด');
    found = await findChapterPage(chapterNum, attempt.url, attempt.name, onProgress, abortSignal);
    if (found) {
      usedSourceName = attempt.name;
      break;
    }
  }

  if (!found) {
    const tried = attempts.map((a) => a.name).join(', ');
    throw new Error(
      `ไม่พบหน้าอ่านตอนที่ ${chapterNum} บน ${tried} (อาจยังไม่มีตอนนี้ หรือโครงสร้างลิงก์เปลี่ยน)`
    );
  }

  const chapterHtml = found.html;
  const successfulChapterUrl = found.url;
  const imageUrls = extractChapterImageUrls(chapterHtml, successfulChapterUrl);
  if (imageUrls.length === 0) {
    throw new Error(`พบหน้าตอนที่ ${chapterNum} แต่ไม่สามารถดึงรูปภาพได้`);
  }

  onProgress?.({
    chapterNum,
    currentImage: 0,
    totalImages: imageUrls.length,
    status: 'downloading_images',
    message: `พบ ${imageUrls.length} รูปภาพ (กรองโฆษณาออกแล้ว) กำลังเริ่มโหลด...`,
  });

  // Download images with concurrency pool (3 concurrent downloads)
  const imageBlobs: Blob[] = new Array(imageUrls.length);
  let downloadedCount = 0;
  let totalBytes = 0;

  const CONCURRENCY = 3;
  let currentIndex = 0;

  async function worker() {
    while (currentIndex < imageUrls.length) {
      if (abortSignal?.aborted) throw new Error('ยกเลิกการดาวน์โหลด');
      const idx = currentIndex++;
      const url = imageUrls[idx];

      try {
        const blob = await downloadImageBlob(url, abortSignal);
        imageBlobs[idx] = blob;
        totalBytes += blob.size;
        downloadedCount++;

        onProgress?.({
          chapterNum,
          currentImage: downloadedCount,
          totalImages: imageUrls.length,
          status: 'downloading_images',
          message: `กำลังโหลดภาพที่ ${downloadedCount}/${imageUrls.length} (${Math.round(
            (downloadedCount / imageUrls.length) * 100
          )}%)...`,
        });
      } catch (err: any) {
        if (err.name === 'AbortError') throw err;
        console.warn(`Failed image ${idx + 1}, using placeholder:`, err.message);
        // Create an empty fallback blob so page order isn't broken
        imageBlobs[idx] = new Blob([], { type: 'image/jpeg' });
        downloadedCount++;
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, imageUrls.length) }, () => worker())
  );

  onProgress?.({
    chapterNum,
    currentImage: imageUrls.length,
    totalImages: imageUrls.length,
    status: 'saving',
    message: 'กำลังบันทึกภาพลงฐานข้อมูลออฟไลน์...',
  });

  const validBlobs = imageBlobs.filter((b) => b && b.size > 0);

  const offlineChapter: OfflineChapter = {
    id: `${mangaId}_${chapterNum}`,
    mangaId,
    mangaTitle,
    chapterNum,
    sourceName: usedSourceName,
    sourceUrl: successfulChapterUrl,
    downloadedAt: new Date().toISOString(),
    imageCount: validBlobs.length,
    images: validBlobs,
    sizeBytes: totalBytes,
  };

  await saveChapter(offlineChapter);

  onProgress?.({
    chapterNum,
    currentImage: imageUrls.length,
    totalImages: imageUrls.length,
    status: 'completed',
    message: `ดาวน์โหลดตอนที่ ${chapterNum} สำเร็จ! (${offlineChapter.imageCount} ภาพ ไร้โฆษณา)`,
  });

  return offlineChapter;
}
